import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { GetLatestDocumentInternalAuditResultByDocumentIdResponseRow } from '@risksmart-app/trpc/src/types';
import type { GetLatestInternalAuditReportDocumentAssessmentResultByDocumentIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC document internal audit result data to match the GraphQL query structure
 */
const mapTrpcDocumentInternalAuditResultToGraphQL = (
  trpcData: GetLatestDocumentInternalAuditResultByDocumentIdResponseRow[]
): GetLatestInternalAuditReportDocumentAssessmentResultByDocumentIdQuery => {
  return {
    document_internal_audit_result: trpcData,
  };
};

export const useGetLatestDocumentInternalAuditResultByDocumentId = (
  documentId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.internalAuditResult.latestDocumentInternalAuditResultByDocumentId.queryOptions(
      {
        documentId,
      }
    ),
    enabled: trpcEnabled && !skip,
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData
    ? mapTrpcDocumentInternalAuditResultToGraphQL(trpcData)
    : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
